import { ip_back } from "../config";

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

async function parseJson(resp) {
  try {
    return await resp.json();
  } catch {
    return {};
  }
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal?.addEventListener?.(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

export function parseSegmentResultJson(resultJson) {
  if (!resultJson) return null;
  if (typeof resultJson === "object") return resultJson;
  try {
    return JSON.parse(resultJson);
  } catch {
    return null;
  }
}

export async function fetchSegmentResults({
  messageId = null,
  teId = null,
  videoUrl = null,
} = {}) {
  const q = new URLSearchParams();
  if (messageId) q.set("message_id", String(messageId));
  if (teId) q.set("te_id", String(teId));
  if (videoUrl) q.set("video_url", String(videoUrl));
  const resp = await fetch(
    `${ip_back}api/mobileAPI/detection/results?${q.toString()}`,
    { headers: authHeaders() }
  );
  const json = await parseJson(resp);
  if (!resp.ok || json.status === false) {
    throw new Error(json.message || "Failed to load segment analysis");
  }
  const data = json.data || {};
  return {
    ...data,
    result: parseSegmentResultJson(data.result_json),
  };
}

export async function enqueueSegmentJob({
  messageId = null,
  teId,
  videoUrl = null,
  twoPass = true,
  tableCorners = null,
} = {}) {
  const resp = await fetch(`${ip_back}api/mobileAPI/detection/jobs`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      message_id: messageId || null,
      te_id: teId,
      video_url: videoUrl || null,
      two_pass: twoPass,
      table_corners: tableCorners || null,
    }),
  });
  const json = await parseJson(resp);
  if (!resp.ok || json.status === false) {
    if (resp.status === 502 || resp.status === 503) {
      throw new Error(
        "The analysis server restarted. Wait a few seconds and tap Detect points again."
      );
    }
    throw new Error(json.message || "Failed to enqueue segment analysis");
  }
  return json.data || {};
}

export async function tryLoadExistingSegment({
  messageId = null,
  teId,
  videoUrl = null,
} = {}) {
  try {
    const wrap = await fetchSegmentResults({ messageId, teId, videoUrl });
    if (wrap?.status === "complete" && wrap.result) {
      return wrap.result;
    }
    return null;
  } catch {
    return null;
  }
}

export async function pollSegmentJobUntilDone(
  { messageId, teId, videoUrl } = {},
  { intervalMs = 2000, timeoutMs = 300000, signal } = {}
) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const wrap = await fetchSegmentResults({ messageId, teId, videoUrl });
    if (wrap?.status === "complete" && wrap.result) {
      return wrap.result;
    }
    if (wrap?.status === "failed") {
      throw new Error(wrap.error_message || "Segment analysis failed");
    }
    await delay(intervalMs, signal);
  }
  throw new Error("Segment analysis timed out");
}
